import { type AnyRoute, createRoute, redirect } from "@tanstack/react-router";
import { type ComponentType, lazy, Suspense } from "react";
import { ADMIN_PAGES, ADMIN_ROOT, type AdminPageKey } from "./lib/admin-pages.js";
import { AdminGate } from "./shell/admin-gate.js";

/**
 * The operator panel's routes. Every page is in every build: the panel's
 * switches went on 2026-10-10 with the Node API they kept it apart from.
 *
 * The parent is a type parameter, not `AnyRoute`, so the router's route tree keeps
 * its exact types.
 */

function loadLayout() {
  return lazy(() =>
    import("./shell/admin-layout.js").then((m) => ({ default: m.AdminLayout })),
  );
}

function loadScreens(): Readonly<Record<AdminPageKey, ComponentType>> {
  return {
    overview: lazy(() =>
      import("./screens/overview/overview-screen.js").then((m) => ({
        default: m.OverviewScreen,
      })),
    ),
    spend: lazy(() =>
      import("./screens/spend/spend-screen.js").then((m) => ({
        default: m.SpendScreen,
      })),
    ),
    model: lazy(() =>
      import("./screens/model/model-screen.js").then((m) => ({
        default: m.ModelScreen,
      })),
    ),
    capture: lazy(() =>
      import("./screens/capture/capture-screen.js").then((m) => ({
        default: m.CaptureScreen,
      })),
    ),
    jobs: lazy(() =>
      import("./screens/jobs/jobs-screen.js").then((m) => ({
        default: m.JobsScreen,
      })),
    ),
    accounts: lazy(() =>
      import("./screens/accounts/accounts-screen.js").then((m) => ({
        default: m.AccountsScreen,
      })),
    ),
    customers: lazy(() =>
      import("./screens/customers/customers-screen.js").then((m) => ({
        default: m.CustomersScreen,
      })),
    ),
    usage: lazy(() =>
      import("./screens/usage/usage-screen.js").then((m) => ({
        default: m.UsageScreen,
      })),
    ),
    emails: lazy(() =>
      import("./screens/emails/emails-screen.js").then((m) => ({
        default: m.EmailsScreen,
      })),
    ),
    engines: lazy(() =>
      import("./screens/engines/engines-screen.js").then((m) => ({
        default: m.EnginesScreen,
      })),
    ),
    services: lazy(() =>
      import("./screens/services/services-screen.js").then((m) => ({
        default: m.ServicesScreen,
      })),
    ),
    storage: lazy(() =>
      import("./screens/storage/storage-screen.js").then((m) => ({
        default: m.StorageScreen,
      })),
    ),
    audit: lazy(() =>
      import("./screens/audit/audit-screen.js").then((m) => ({
        default: m.AuditScreen,
      })),
    ),
  };
}

const Layout = loadLayout();
const SCREENS = loadScreens();

function Frame() {
  return (
    <AdminGate>
      <Suspense fallback={null}>
        <Layout />
      </Suspense>
    </AdminGate>
  );
}

/** `/admin` and everything under it, as one route with its pages as children. */
export function adminRoute<TParent extends AnyRoute>(parent: TParent) {
  const layout = createRoute({
    getParentRoute: () => parent,
    path: ADMIN_ROOT,
    component: Frame,
  });
  const pages = ADMIN_PAGES.map((page) => {
    const Screen = SCREENS[page.key];
    return createRoute({
      getParentRoute: () => layout,
      path: page.key === "overview" ? "/" : page.path.slice(ADMIN_ROOT.length),
      component: () => <Screen />,
    });
  });
  // Onboarding became the lower half of Usage (2026-10-13); its old address
  // still lands there.
  const onboardingMoved = createRoute({
    getParentRoute: () => layout,
    path: "/onboarding",
    beforeLoad: () => {
      redirect({ href: `${ADMIN_ROOT}/usage`, replace: true, throw: true });
    },
  });
  return layout.addChildren([...pages, onboardingMoved]);
}
